import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';

interface AggregatedItem {
  faculty: string;
  count: number;
}

interface FacultyBarChartProps {
  title: string;
  description: string;
  data: AggregatedItem[];
  yearSuffix?: string;
  seriesLabel: string;
}

export default function FacultyBarChart({
  title,
  description,
  data,
  yearSuffix,
  seriesLabel,
}: FacultyBarChartProps) {
  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = { count: { label: seriesLabel } };
    for (const d of data) {
      cfg[d.faculty] = {
        label: d.faculty,
        color: FACULTY_COLORS[d.faculty] || DEFAULT_NODE_COLOR,
      };
    }
    return cfg;
  }, [data, seriesLabel]);

  const colored = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        fill: FACULTY_COLORS[d.faculty] || DEFAULT_NODE_COLOR,
      })),
    [data],
  );

  return (
    <Card className="border-ctu-border shadow-none">
      <CardContent className="flex flex-col gap-3 px-6 pt-5 pb-4">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {title}
            {yearSuffix && (
              <span className="ml-2 font-normal normal-case tracking-normal text-ctu-text-muted/80">
                · {yearSuffix}
              </span>
            )}
          </span>
          <span className="text-xs text-ctu-text-muted leading-snug">
            {description}
          </span>
        </div>

        {data.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-ctu-text-muted">
            —
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto w-full h-[280px]"
          >
            <BarChart
              data={colored}
              margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              barCategoryGap={4}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="faculty"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                fontSize={11}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                allowDecimals={false}
                tickMargin={4}
              />
              <ChartTooltip
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    nameKey="faculty"
                    labelFormatter={(value) => String(value)}
                  />
                }
              />
              <Bar dataKey="count" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
